import { FileBasic } from '@commenda/types/src/file/types';
import type { TransactionSourcePlatform } from '../../sales-tax/types';
import { ExemptionInvitationStatus } from '@prisma/client';
import {
  CertificateTypeEnum,
  ExemptionIdTypeEnum,
  ExemptionReasonEnum,
  ExemptionStatusEnum,
  VerificationStatusEnum,
} from '../../common/types';
import { JobEntity, JobOperation, JobStatus } from '../../new-sales-tax/job/types';
import type { GetSubmissionResponse } from '../../exemption-portal/types';

// ─── Shipping Address ───

export type TIndirectTaxCustomerShippingAddress = {
  id?: number;
  address_line_1: string;
  address_line_2?: string;
  address_line_3?: string;
  postal_code: string;
  city: string;
  state: string;
  country: string;
};

// ─── Customer CRUD Types ───

export interface TIndirectTaxCustomerCreatePayload {
  name: string;
  email: string;
  shipping_address?: TIndirectTaxCustomerShippingAddress;
  corporation_id: number;
}

export interface TIndirectTaxCustomerUpdatePayload {
  name?: string;
  email?: string;
  shipping_address?: TIndirectTaxCustomerShippingAddress;
}

export enum TIndirectTaxCustomerExtend {
  SHIPPING_ADDRESS = 'shipping_address',
  EXEMPTION_METADATA = 'exemption_metadata',
}

export interface TIndirectTaxCustomerExemptionMetadata {
  total_certificates: number;
  certificate_types: CertificateTypeEnum[];
  total_exempted_states: number;
  expired_exemptions_count: number;
  inactive_exemptions_count: number;
  exempted_states: string[];
  active_exemptions_count: number;
  non_expired_exemptions_count: number;
}

export interface TIndirectTaxCustomerEntity {
  id: string;
  name: string;
  email: string;
  corporation_id: string;
  created_at: number;
  shipping_address?: TIndirectTaxCustomerShippingAddress;
  source_platform?: TransactionSourcePlatform;
  source_platform_id?: string;
  business_identification_number?: string;
  exemption_metadata?: TIndirectTaxCustomerExemptionMetadata;
}

export type TIndirectTaxCustomerCreateResponse = TIndirectTaxCustomerEntity;

export interface TIndirectTaxCustomerGetResponse {
  data: TIndirectTaxCustomerEntity;
  message: string;
}

export interface TIndirectTaxCustomerUpdateResponse {
  message: string;
}

// ─── Customer Exemption Types ───

export enum TIndirectTaxCustomerExemptionExpirationStatus {
  EXPIRED = 'EXPIRED',
  NON_EXPIRED = 'NON_EXPIRED',
}

export interface TIndirectTaxCustomerExemptionFilters {
  certificateTypes?: CertificateTypeEnum[];
  exemptionJurisdiction?: string[];
  exemptionStatus?: ExemptionStatusEnum;
  expirationStatus?: TIndirectTaxCustomerExemptionExpirationStatus;
  corporationId: number;
  hasExemption?: boolean;
}

export interface TIndirectTaxCustomerExemption {
  exemption: {
    country: string;
    state: string;
    reason: ExemptionReasonEnum;
    is_active: boolean;
    end_date?: string;
    identification_type: ExemptionIdTypeEnum;
    identification_number: string;
    is_expired: boolean;
    created_at: number;
    certificate: {
      id: string;
      type: CertificateTypeEnum;
      verification_status: VerificationStatusEnum;
      exemption_certificate_number?: string;
      effective_date: string;
      file_id?: string;
      file_name?: string;
      created_at: number;
    };
  };
}

export interface TIndirectTaxCustomerExemptionListData {
  exemptions: TIndirectTaxCustomerExemption[];
  cursor?: string;
  total_exemptions: number;
}

export interface TIndirectTaxCustomerExemptionResponse {
  data: TIndirectTaxCustomerExemptionListData;
  message: string;
}

// ─── Seller-Customer Relationship Types (Exemption Portal) ───

export type TIndirectTaxSellerCustomerRelationship = {
  id: string;
  requestId: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  status: ExemptionInvitationStatus;
  isActive: boolean;
  invitedAt: Date;
  acceptedAt: Date;
  sourcePlatform?: TransactionSourcePlatform;
  sourcePlatformId?: string;
  certificates: {
    total: number;
    pending: number;
    rejected: number;
    lastActivity: Date | null;
  };
};

export interface TIndirectTaxSellerCustomerRelationshipListData {
  customers: TIndirectTaxSellerCustomerRelationship[];
  cursor?: string;
  total_customers: number;
}

export interface TIndirectTaxSellerCustomerRelationshipListResponse {
  data: TIndirectTaxSellerCustomerRelationshipListData;
  message: string;
}

// ─── Bulk Operations ───

export interface TIndirectTaxCustomerBulkActionParams {
  corporation_id: number;
  customer_ids?: string[];
  excluded_ids?: string[];
  search_term?: string;
}

export type TIndirectTaxCustomerColumnMapping = {
  fromColumn: string;
  toColumn: string;
};

export interface TIndirectTaxCustomerBulkUploadProps {
  file: FileBasic;
  corporationId: number;
  columnMapping?: TIndirectTaxCustomerColumnMapping[];
}

export interface TIndirectTaxCustomerBulkJobResponse {
  id: string;
  org_id: string;
  corporation_id: string;
  entity: JobEntity;
  operation: JobOperation;
  status: JobStatus;
  file_name: string;
  failure_reasons: string[] | null;
}

export interface TIndirectTaxCustomerBulkUploadResponse {
  data: {
    job: TIndirectTaxCustomerBulkJobResponse;
  };
  message: string;
}

// ─── Submissions / Certificates Types (aligned with exemption-portal GetSubmissionResponse) ───

export type TIndirectTaxCustomerGetSubmissionResponse = GetSubmissionResponse;

// ─── Email Campaign Types ───

export interface TIndirectTaxCustomerSendExemptionInvitationResponse {
  campaignId: string;
  relationshipsCreated: number;
  invitationsQueued: number;
  batchesQueued: number;
}

// ─── Hook Param Types ───

export enum TIndirectTaxCustomerSortBy {
  NAME = 'name',
  CERTIFICATE_COUNT = 'certificate_count',
  EMAIL = 'email',
  SOURCE_PLATFORM = 'source_platform',
  SOURCE_PLATFORM_ID = 'source_platform_id',
}

export enum TIndirectTaxCustomerSortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface TIndirectTaxCustomersHookParams {
  corporationId: number;
  limit?: number;
  cursor?: string | null;
  searchTerm?: string | null;
  sortBy?: TIndirectTaxCustomerSortBy | null;
  sortOrder?: TIndirectTaxCustomerSortOrder | null;
}

export interface TIndirectTaxCustomerHookParams {
  customerId?: string;
  corporationId?: number;
}

export interface TIndirectTaxCustomerExemptionsHookParams {
  cursor?: string | null;
  limit?: number;
  filters: TIndirectTaxCustomerExemptionFilters & { customerId: string };
}

// ─── Mutation Result Types ───

export interface TIndirectTaxCustomerDeleteResult {
  success: boolean;
  error?: string;
}

export interface TIndirectTaxCustomerBulkDeleteResult {
  success: boolean;
  message?: string;
  error?: string;
}

// ─── Certificate Form Data (shape of ExemptionCertificateSubmission.formData JSON) ───

export interface TIndirectTaxCertificateFormDataJurisdiction {
  state?: string;
}

export interface TIndirectTaxCertificateFormData {
  type?: string;
  reason?: string;
  jurisdictions?: TIndirectTaxCertificateFormDataJurisdiction[];
}
